import { Row, Spin, Typography, Table, Card, Button, message, Popconfirm, Modal, Col, Radio } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";
import { useEffect, useState } from "react";
import HTTPClient from "../../../utils/HTTPClient";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const Templates = () => {
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [hovered, setHovered] = useState(null);
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [plan, setPlan] = useState("yearly");
    const [selectedCategory, setSelectedCategory] = useState("All");

    const { t } = useTranslation();
    const navigate = useNavigate();

    const categories = [
        "All",
        "Business",
        "Landing",
        "Education",
        "Food",
        "Art & Music",
        "Portfolio",
        "Sport & Health",
        "Travel"
    ]

    const fetch = async () => {
        setLoading(true);

        const templates_res = await HTTPClient.get("/builder/get_templates");
        setTemplates(templates_res.data);

        setLoading(false);
    };

    useEffect(() => {
        fetch();
    }, []);

    const handlePlanChange = (e) => {
        setPlan(e.target.value);
    };

    const createProject = async (template) => {
        setLoading(true);
        setSelectedTemplate(template);
        HTTPClient.post("/builder/projects", {
            template_id: template,
        })
            .then((response) => {
                if (response.status === 201) {
                    navigate("/pro/builder/list");
                }
                setLoading(false);
            })
            .catch((error) => {
                if (error.response && error.response.status === 402) {
                    setShowBuyModal(true);
                } else {
                    message.error(t("builder.create.error"));
                }
                setLoading(false);
            });
    };

    const buyProject = async (subscription_type) => {
        setLoading(true);
        const body = {
            template_id: selectedTemplate,
            subscription_type,
        };
        try {
            await HTTPClient.post("/stripe/create_builder_checkout_session", body);
            message.success(t("builder.list.update.success"));
            fetch();
        } catch (e) {
            // redirect to checkout portal
            if (e.response.status === 303) {
                window.location.replace(e.response.data);
                return;
            }
            const { error_code } = e?.response?.data;
            message.error(t([`errors.${error_code}`, "builder.list.error"]));
        }
        setLoading(false);
    };

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    if (loading) return <Spin size="large" />;

    return (
        <>
            <Row style={{ padding: isLargeScreen ? "20px 50px" : "10px 10px" }}>
                <Row>
                    <Typography.Text style={{ fontSize: "36px", fontWeight: "bold" }}>
                        {t("builder.create.templates.title")}
                    </Typography.Text>
                </Row>
                <Row style={{ width: "100%", marginTop: "10px" }}>
                    <Typography.Text>{t("builder.create.templates.subtitle")}</Typography.Text>
                </Row>
                <Row style={{ margin: "10px" }}>
                    {categories.map((category, index) => (
                        <Button
                            key={index}
                            type="primary"
                            style={{ margin: "10px" }}
                            disabled={category === selectedCategory}
                            onClick={()=>setSelectedCategory(category)}>
                            {category}
                        </Button>
                    ))}
                </Row>
                <Row style={{ display: "flex", justifyContent: "space-between" }}>
                    {templates.filter((t)=>t.categories.map(d=>d.title).includes(selectedCategory)).map((template, index) => (
                        <Col xs={24} s={24} md={24} lg={7} xl={7}>
                            <Card
                                bordered={true}
                                style={{
                                    width: "100%",
                                    borderRadius: "10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: "500px",
                                    marginBottom: "20px",
                                }}
                                onMouseEnter={() => setHovered(index)}
                                onMouseLeave={() => setHovered(null)}>
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        backgroundImage: `url(${template.featured_image})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        opacity: hovered === index ? 0.1 : 1,
                                    }}
                                />
                                {hovered === index && (
                                    <>
                                        <Row>
                                            <Card
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                }}>
                                                <Typography.Text>{template.title}</Typography.Text>
                                            </Card>
                                        </Row>

                                        <Row>
                                            <a
                                                href={template.preview_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ width: "100%" }}>
                                                <Button
                                                    type="primary"
                                                    style={{ marginTop: "10px", width: "100%" }}>
                                                    {t("builder.create.templates.actions.preview")}
                                                </Button>
                                            </a>
                                        </Row>
                                        <Row>
                                            <Popconfirm
                                                title={t("builder.create.templates.popconfirm_message")}
                                                onConfirm={() => createProject(template.project)}
                                                okText={t("builder.create.templates.ok")}
                                                cancelText="No"
                                                okButtonProps={{ loading: loading }}>
                                                <Button
                                                    type="primary"
                                                    style={{ marginTop: "10px", width: "100%" }}>
                                                    {t("builder.create.templates.actions.use_template")}
                                                </Button>
                                            </Popconfirm>
                                        </Row>
                                    </>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Row>
            <Modal
                title={t("builder.create.buy_modal.title")}
                open={showBuyModal}
                footer={null}
                onCancel={() => setShowBuyModal(false)}>
                <Row align="middle" style={{ textAlign: "center" }}>
                        <Col span={8}>
                            <img src="/alert.png" alt="Hurry up" style={{ height: "120px" }} />                        
                        </Col>
                        <Col span={16}>
                            <Row>
                                <Typography.Text>{t("builder.create.buy_modal.text")}</Typography.Text>
                            </Row>
                        </Col>
                    </Row>
                <Row style={{ display: "flex", justifyContent: "center", marginTop: "10px"}}>
                    <Radio.Group onChange={handlePlanChange} value={plan} buttonStyle="solid">
                        <Radio.Button value="monthly">{t("builder.list.go_pro_modal.month")}</Radio.Button>
                        <Radio.Button value="yearly">{t("builder.list.go_pro_modal.year")}</Radio.Button>
                    </Radio.Group>
                </Row>
                <Row style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
                    <Col span={16}>
                        <Row>
                            <Col span={3}></Col>
                            <Col span={4}>
                                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                            </Col>
                            <Col span={15}>
                                <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{plan==="monthly" ? t("builder.list.go_pro_modal.pro1a") : t("builder.list.go_pro_modal.pro1b") }</Typography.Text>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "15px" }}>
                            <Col span={3}></Col>
                            <Col span={4}>
                                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                            </Col>
                            <Col span={15}>
                                <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro2")}</Typography.Text>
                            </Col>
                        </Row>
                        <Row style={{ marginTop: "15px" }}>
                            <Col span={3}></Col>    
                            <Col span={4}>
                                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                            </Col>
                            <Col span={15}>
                                <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro3")}</Typography.Text>
                            </Col>
                        </Row>
                        {plan === "yearly" && 
                            <Row style={{ marginTop: "10px" }}>
                                <Col span={3}></Col>    
                                <Col span={4}>
                                    <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                                </Col>
                                <Col span={15}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro4")}</Typography.Text>
                                </Col>
                            </Row>    
                        }
                    </Col>
                </Row>
                <Row style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
                    <Button type="primary" onClick={() => buyProject(plan)}>
                        {t("builder.create.buy_modal.call_to_action")}
                    </Button>
                </Row>
            </Modal>
        </>
    );
};

export { Templates };
